/**
 * Point-registered elevation grid (Copernicus GLO-30 clip). Pure functions, no DOM and no network.
 *
 * The grid is defined by the CENTRE of its first sample (north-west), a step in degrees and its size; rows run north
 * to south. Sample (row, col) is centred exactly at lon = lon0 + col·step, lat = lat0 − row·step. That centre comes
 * from GDAL when the clip is built (scripts/elevation/build_dem_clip.py), so there is no TIFF raster-space convention
 * to interpret here and no half-cell offset to add or subtract.
 */

export interface DemMetadata {
  readonly id: string;
  readonly registration: 'point';
  readonly crs: { readonly horizontal: string; readonly vertical: string; readonly units: string };
  readonly grid: {
    readonly firstCentre: readonly [number, number];
    readonly lastCentre: readonly [number, number];
    readonly step: readonly [number, number];
    readonly width: number;
    readonly height: number;
  };
  readonly encoding: { readonly type: 'int16le'; readonly compression: 'gzip'; readonly scale: number; readonly nodata: number; readonly water: number };
  readonly files: { readonly bin: { readonly bytes: number; readonly sha256: string }; readonly raw: { readonly bytes: number; readonly sha256: string } };
  readonly source: {
    readonly product: string;
    readonly release: string;
    /** Official tiles mosaicked west to east (MD5 = S3 ETag), with the mosaic columns each one supplies. */
    readonly tiles: readonly { readonly tile: string; readonly url: string; readonly md5: string; readonly mosaicColumns: readonly [number, number] }[];
  };
  readonly interpolation: string;
  readonly attribution: string;
  readonly liability: string;
}

export interface DemGrid {
  readonly metadata: DemMetadata;
  readonly values: Int16Array;
  readonly lon0: number;
  readonly lat0: number;
  readonly step: number;
  readonly width: number;
  readonly height: number;
}

export type ElevationSample =
  | { readonly status: 'ok'; readonly elevation: number }
  /** Ocean or lake surface (flattened in the DEM): not terrain, so no height is given. */
  | { readonly status: 'water' }
  /** Outside the clip (beyond the outermost sample centres) or a void: never extrapolated. */
  | { readonly status: 'outside' }
  | { readonly status: 'nodata' };

export type Interpolation = 'bilinear' | 'nearest';

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Validates the JSON sidecar; throws a readable error instead of accepting a grid it cannot place correctly. */
export function parseDemMetadata(input: unknown): DemMetadata {
  const meta = input as Partial<DemMetadata> | null;
  const grid = meta?.grid; const encoding = meta?.encoding;
  if (!meta || meta.registration !== 'point' || !grid || !encoding) throw new Error('Metadatos DEM inválidos: falta la definición de malla.');
  const [lon0, lat0] = grid.firstCentre ?? []; const [lon1, lat1] = grid.lastCentre ?? []; const [stepX, stepY] = grid.step ?? [];
  if (![lon0, lat0, lon1, lat1, stepX, stepY].every(finite) || !(stepX! > 0) || stepX !== stepY) throw new Error('Metadatos DEM inválidos: centros o paso no válidos.');
  if (!Number.isInteger(grid.width) || !Number.isInteger(grid.height) || grid.width < 2 || grid.height < 2) throw new Error('Metadatos DEM inválidos: dimensiones.');
  // The declared last centre must follow from the first one: catches a mixed corner/centre definition.
  if (Math.abs(lon0! + (grid.width - 1) * stepX! - lon1!) > stepX! / 1000 || Math.abs(lat0! - (grid.height - 1) * stepY! - lat1!) > stepY! / 1000) throw new Error('Metadatos DEM inválidos: el último centro no coincide con la malla.');
  if (encoding.type !== 'int16le' || !finite(encoding.scale) || !finite(encoding.nodata) || !finite(encoding.water)) throw new Error('Metadatos DEM inválidos: codificación.');
  if (meta.crs?.horizontal !== 'EPSG:4326') throw new Error('Metadatos DEM inválidos: CRS horizontal no soportado.');
  return meta as DemMetadata;
}

export function createDemGrid(metadata: DemMetadata, values: Int16Array): DemGrid {
  const { width, height, firstCentre, step } = metadata.grid;
  if (values.length !== width * height) throw new Error(`El archivo DEM tiene ${values.length} muestras; se esperaban ${width * height}.`);
  return { metadata, values, lon0: firstCentre[0], lat0: firstCentre[1], step: step[0], width, height };
}

/** Little-endian int16 view regardless of the platform byte order (all browsers in practice are little-endian). */
export function int16FromLittleEndian(bytes: Uint8Array): Int16Array {
  if (bytes.byteLength % 2) throw new Error('Archivo DEM truncado.');
  const littleEndian = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;
  if (littleEndian && bytes.byteOffset % 2 === 0) return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength); const out = new Int16Array(bytes.byteLength / 2);
  for (let index = 0; index < out.length; index += 1) out[index] = view.getInt16(index * 2, true);
  return out;
}

/** Fractional grid position of a WGS84 coordinate: integer values are sample centres. */
export const gridPosition = (grid: DemGrid, lon: number, lat: number): { readonly col: number; readonly row: number } =>
  ({ col: (lon - grid.lon0) / grid.step, row: (grid.lat0 - lat) / grid.step });

/** Centre of sample (row, col) in WGS84. */
export const sampleCentre = (grid: DemGrid, row: number, col: number): { readonly lon: number; readonly lat: number } =>
  ({ lon: grid.lon0 + col * grid.step, lat: grid.lat0 - row * grid.step });

// Tolerance (in pixels) for coordinates that land on a sample centre or grid line within floating-point noise:
// 1e-6 px ≈ 0.03 mm. Snapping keeps the weight of the far neighbours exactly 0 (a centre next to water stays valid).
const EDGE_EPSILON = 1e-6;
const snap = (value: number): number => { const nearest = Math.round(value); return Math.abs(value - nearest) < EDGE_EPSILON ? nearest : value; };

const raw = (grid: DemGrid, row: number, col: number): number => grid.values[row * grid.width + col]!;

/**
 * Elevation at a WGS84 coordinate (metres above EGM2008).
 * - bilinear: weighted mean of the four surrounding sample centres; if any of them is water or no-data the result is
 *   not a terrain value and is reported as such (never mixed with a lake surface).
 * - nearest: the sample whose centre is closest (GDAL's pixel for a point-registered raster).
 * Coordinates outside the outermost centres are `outside`: the grid is never extrapolated.
 */
export function sampleElevation(grid: DemGrid, lon: number, lat: number, interpolation: Interpolation = 'bilinear'): ElevationSample {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return { status: 'outside' };
  let { col, row } = gridPosition(grid, lon, lat);
  const maxCol = grid.width - 1; const maxRow = grid.height - 1;
  if (col < -EDGE_EPSILON || row < -EDGE_EPSILON || col > maxCol + EDGE_EPSILON || row > maxRow + EDGE_EPSILON) return { status: 'outside' };
  col = Math.min(Math.max(snap(col), 0), maxCol); row = Math.min(Math.max(snap(row), 0), maxRow);
  const { scale, nodata, water } = grid.metadata.encoding;
  const decode = (value: number): ElevationSample => value === water ? { status: 'water' } : value === nodata ? { status: 'nodata' } : { status: 'ok', elevation: value * scale };
  if (interpolation === 'nearest') return decode(raw(grid, Math.round(row), Math.round(col)));
  const col0 = Math.min(Math.floor(col), maxCol - 1); const row0 = Math.min(Math.floor(row), maxRow - 1);
  const fx = col - col0; const fy = row - row0;
  const corners = [raw(grid, row0, col0), raw(grid, row0, col0 + 1), raw(grid, row0 + 1, col0), raw(grid, row0 + 1, col0 + 1)];
  // A neighbour with zero weight does not affect the value (exact centres or grid lines next to water stay valid).
  const weights = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
  let sum = 0; let sawWater = false;
  for (let index = 0; index < 4; index += 1) {
    if (weights[index] === 0) continue;
    const value = corners[index]!;
    if (value === water) { sawWater = true; continue; }
    if (value === nodata) return { status: 'nodata' };
    sum += value * weights[index]!;
  }
  if (sawWater) return { status: 'water' };
  return { status: 'ok', elevation: sum * scale };
}

/** Covered WGS84 extent (outermost sample centres): [west, south, east, north]. */
export const gridCoverage = (grid: DemGrid): readonly [number, number, number, number] =>
  [grid.lon0, grid.lat0 - (grid.height - 1) * grid.step, grid.lon0 + (grid.width - 1) * grid.step, grid.lat0];
